import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/settings'
import { useNotificationStore } from '@/stores/notifications'
import axios from '@/axios'
import { getPasskey } from '@/utils/webauthn'

const PUBLIC_AUTH_PATHS = [
  '/',
  '/forgot_password',
  '/reset_password'
]

function normalizeUser(data) {
  const source =
    data?.actor ||
    data?.user ||
    data ||
    {}

  const userDetailId =
    source.user_detail_id ||
    data?.user_detail_id ||
    null

  return {
    username:
      source.username ||
      data?.username ||
      null,

    user_detail_id: userDetailId,

    external_user_id:
      source.external_user_id ||
      data?.external_user_id ||
      null,

    actor_type:
      source.actor_type ||
      data?.actor_type ||
      null,

    avatar:
      source.avatar ?? null,

    active_tab_branch_id:
      source.active_tab_branch_id ||
      data?.active_tab_branch_id ||
      null,

    can:
      data?.can ||
      source?.can ||
      {},

    managed_branch_ids:
      data?.managed_branch_ids ||
      source?.managed_branch_ids ||
      []
  }
}

function announceSessionChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-session-changed'))
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    loading: false,
    initialized: false
  }),

  getters: {
    isLoggedIn: state => {
      return Boolean(state.user)
    },

    user_detail_id: state => {
      return state.user?.user_detail_id || null
    },

    avatar: state => {
      return state.user?.avatar || null
    },

    activeTabBranchId: state => {
      return state.user?.active_tab_branch_id || null
    },

    can: state => {
      return state.user?.can || {}
    },

    managedBranchIds: state => {
      return state.user?.managed_branch_ids || []
    },

    canViewSiteAdmin: state => {
      return Boolean(
        state.user?.can?.view_site_admin
      )
    },

    canManageSiteAdmin: state => {
      return Boolean(
        state.user?.can?.manage_site_admin
      )
    },

    canManageBranchAdmin: state => {
      return Boolean(
        state.user?.managed_branch_ids?.length
      )
    },

    canGrantSiteAdmin: state => {
      return Boolean(
        state.user?.can?.grant_site_admin
      )
    },

    canRevokeSiteAdmin: state => {
      return Boolean(
        state.user?.can?.revoke_site_admin
      )
    },

    canSetBranchAdmin: state => {
      return Boolean(
        state.user?.can?.set_branch_admin
      )
    },

    canSetExecutor: state => {
      return Boolean(
        state.user?.can?.set_executor
      )
    },

    canSetArchivist: state => {
      return Boolean(
        state.user?.can?.set_archivist
      )
    },

    canSetGenealogist: state => {
      return Boolean(
        state.user?.can?.set_genealogist
      )
    },

    canCreateExternalUser: state => {
      return Boolean(
        state.user?.can?.create_external_user
      )
    },

    canReviewChanges: state => {
      return Boolean(
        state.user?.can?.review_changes
      )
    },

    adminRoleCodes: state => {
      return Array.isArray(state.user?.can?.admin_role_codes)
        ? state.user.can.admin_role_codes.map(Number)
        : []
    }
  },

  actions: {
    async login(payload) {
      this.loading = true

      try {
        const { data } = await axios.post(
          '/api/auth/login',
          payload,
          {
            withCredentials: true
          }
        )

        if (data?.requires_password_reset || data?.requires_two_factor) {
          this.user = null
          this.initialized = true

          return data
        }

        await this.acceptAuthenticatedResponse(data)

        return data
      } finally {
        this.loading = false
      }
    },

    async verifyTwoFactor(challengeId, code) {
      const { data } = await axios.post('/api/auth/2fa/verify', {
        challenge_id: challengeId,
        code
      })
      await this.acceptAuthenticatedResponse(data)
      return data
    },

    async verifyPasskeyChallenge(options) {
      const credential = await getPasskey(options.public_key)
      const { data } = await axios.post('/api/auth/passkeys/verify', {
        challenge_id: options.challenge_id,
        credential
      })
      await this.acceptAuthenticatedResponse(data)
      return data
    },

    async loginWithPasskey(username) {
      const { data: options } = await axios.post('/api/auth/passkeys/options', { username })
      return this.verifyPasskeyChallenge(options)
    },

    async hasPasskey(username) {
      const { data } = await axios.post('/api/auth/passkeys/availability', { username })
      return Boolean(data?.available)
    },

    async acceptAuthenticatedResponse(data) {
      const settings = useSettingsStore()
      const notifications = useNotificationStore()

      settings.resetStore()
      notifications.resetStore()
      this.user = normalizeUser(data)
      this.initialized = true
      announceSessionChange()

      if (this.user.user_detail_id) {
        void Promise.allSettled([
          settings.load(true),
          notifications.init()
        ])
      }
    },

    async refreshMe() {
      const { data } = await axios.get(
        '/api/auth/me',
        {
          withCredentials: true
        }
      )

      this.user = normalizeUser(data)
      announceSessionChange()

      return data
    },

    async fetchMe() {
      if (this.loading) return

      this.loading = true

      try {
        await axios.get('/api/auth/csrf', {
          withCredentials: true
        })

        await this.refreshMe()

        const settings = useSettingsStore()
        const notifications = useNotificationStore()

        if (this.user?.user_detail_id) {
          await settings.load(true)
          await notifications.init()
        } else {
          settings.resetStore()
          notifications.resetStore()
        }
      } catch {
        this.user = null
      } finally {
        this.loading = false
        this.initialized = true
      }
    },

    async initAuth() {
      if (
        this.initialized ||
        this.loading
      ) {
        return
      }

      if (
        PUBLIC_AUTH_PATHS.includes(
          window.location.pathname
        )
      ) {
        this.user = null
        this.initialized = true
        return
      }

      await this.fetchMe()
    },

    async logout() {
      try {
        await axios.post(
          '/api/auth/logout',
          {},
          {
            withCredentials: true
          }
        )
      } finally {
        useNotificationStore().resetStore()
        useSettingsStore().resetStore()
        this.user = null
        this.initialized = true
        announceSessionChange()
      }
    }
  }
})
